/**
 * Ordering the audit table.
 *
 * Every column sorts except the one holding the expand control, which has
 * nothing to order by. That is the house rule for every table in Atlas.
 *
 * **The sort is over the entries LOADED, not over the whole log**, and the
 * screen says so beneath the table. It has to be: the log is read by keyset
 * in `occurred_at DESC, id DESC` order, which is the only order a cursor can
 * walk, and a server-side sort by outcome or by action would need either an
 * `OFFSET` over the largest table in the system or an index per column.
 * Loading more entries and then re-sorting keeps working, because the sort is
 * reapplied to everything loaded so far.
 */

import type { AuditLogEntrySummary } from '@/api/client';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

export type SortColumn =
  | 'timestamp'
  | 'actor'
  | 'role'
  | 'action'
  | 'entityType'
  | 'entityId'
  | 'outcome'
  | 'source'
  | 'request';

/** Newest first, which is the order the server reads the log in. */
export const DEFAULT_SORT: SortState<SortColumn> = {
  column: 'timestamp',
  direction: 'desc',
};

export function sortValueOf(entry: AuditLogEntrySummary, column: SortColumn): SortValue {
  switch (column) {
    case 'timestamp':
      // The instant, not the rendered string: the table shows local time and
      // a string sort would file entries by how they happen to be spelled.
      return new Date(entry.occurred_at);
    case 'actor':
      return entry.actor_display;
    case 'role':
      return entry.actor_role_at_time;
    case 'action':
      return entry.action;
    case 'entityType':
      return entry.entity_type;
    case 'entityId':
      return entry.entity_id;
    case 'outcome':
      return entry.outcome;
    case 'source':
      return entry.source_ip;
    case 'request':
      return entry.request_id;
  }
}

export function sortEntries(
  entries: readonly AuditLogEntrySummary[],
  sort: SortState<SortColumn>,
): AuditLogEntrySummary[] {
  return sortRows(entries, sort, sortValueOf);
}
